/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Box, FormLabel, Switch, Typography } from "@mui/material";
import {
  useHasDefaultSets,
  useSearch,
} from "contexts/search/SearchContextProvider";
import { groupBy, sorted } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { useNavigateToSearchPlanPeek } from "hooks/navigation";
import { useIsAdmin, useTenant } from "hooks/settings.tsx";
import { useShowIntermediateEditedPlans } from "hooks/useShowIntermediateEditedPlans";
import { useCallback, useMemo, useState } from "react";
import { LoadedContent } from "src/components/common/loading/loadedContent";
import {
  OptimisationFailureReason,
  Plan,
  useSendPlanNotificationMutation,
} from "store/api/generatedApi";
import { useDefaultPlanName } from "../../plan/name";
import { FailedSearch } from "./FailedSearch";
import { LoadingResults } from "./LoadingResults";
import { PlanCardData } from "./planCard/data";
import { SearchResultsLayout } from "./SearchResultsLayout";
import { SuccessfulSearch } from "./SuccessfulSearch";
import { liftLoadedState } from "models/loaded";
import { useGetRunConditions } from "./RunConditions";

export const SearchResults = () => {
  const {
    results: { results, isStale: stale },
    clientSearchId: searchId,
  } = useSearch();

  const hasDefaultSets = useHasDefaultSets();

  const tenant = useTenant();
  const [sendNotification] = useSendPlanNotificationMutation();
  const navigateToSearchPlanPeek = useNavigateToSearchPlanPeek();
  const defaultPlanName = useDefaultPlanName();
  const runConditions = useGetRunConditions();

  const doNavigate = useCallback(
    (planCard: PlanCardData) => {
      void sendNotification({
        tenantName: tenant,
        planId: planCard.planId,
        planName: planCard.planName ?? defaultPlanName(planCard.planId),
      });
      navigateToSearchPlanPeek(planCard.searchId, planCard.planId);
    },
    [sendNotification, tenant, defaultPlanName, navigateToSearchPlanPeek]
  );

  return (
    <Box sx={{ height: "100%", overflowY: "auto" }}>
      <LoadedContent
        data={liftLoadedState({
          result: results,
          hasDefaultSets,
          runConditions:
            runConditions === null
              ? { status: "loading" }
              : { status: "success", data: runConditions, fetching: false },
        })}
        loading={<LoadingResults includeText={false} />}
        waiting={<LoadingResults includeText={false} />}
        disableFetching
      >
        {({ result, hasDefaultSets, runConditions }, running) => {
          const longestSuboptimalTimeLimit = computeLongestSuboptimalTimeLimit(
            result?.plans
          );
          return (
            <>
              {running ? <LoadingResults includeText /> : null}

              <SearchResultsLayout
                isNewSearch={result === null && !running}
                inputsStale={!running && stale}
                constraintsStale={
                  !running && !hasDefaultSets && searchId !== null
                }
                longestSuboptimalTimeLimit={longestSuboptimalTimeLimit}
                runConditions={runConditions}
              >
                {result == null && !running ? null : (
                  <SearchResultsContent
                    running={running}
                    plans={result?.plans ?? []}
                    errors={result?.errors ?? []}
                    onClickPlan={doNavigate}
                  />
                )}
              </SearchResultsLayout>
            </>
          );
        }}
      </LoadedContent>
    </Box>
  );
};

export const SearchResultsContent = ({
  running,
  plans,
  errors,
  onClickPlan,
}: {
  running: boolean;
  plans: Plan[];
  errors: OptimisationFailureReason[];
  onClickPlan: (planCard: PlanCardData) => void;
}) => {
  const { t } = useTenantTranslation();
  const [showFailedResults, setShowFailedResults] = useState(false);
  const showIntermediateResults = useShowIntermediateEditedPlans();
  const organisedPlans = organisePlans(
    plans,
    showFailedResults,
    showIntermediateResults
  );
  const isAdmin = useIsAdmin();

  // Determine plans to show in which section.
  // If the search was successful, show all plans.
  // If the search was not successful, show edited
  //  plans but not base plans.
  // Fallback plans are always shown.
  const searchSuccessful = useMemo(() => {
    return organisedPlans.length > 0 || errors.length === 0;
  }, [errors, organisedPlans]);

  const nonFallbackPlansToShow = useMemo(() => {
    const nonFallbackMatching = organisedPlans.filter(
      (p) => p.metadata.plan_type !== "fallback"
    );

    if (searchSuccessful || showFailedResults) {
      return nonFallbackMatching;
    }
    return nonFallbackMatching.filter((p) => p.metadata.plan_type === "edited");
  }, [organisedPlans, searchSuccessful, showFailedResults]);

  const fallbackPlans = useMemo(() => {
    return organisedPlans.filter((p) => p.metadata.plan_type === "fallback");
  }, [organisedPlans]);

  return (
    <>
      {running && plans.length === 0 ? null : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h3">{t("results")}</Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            {isAdmin ? (
              <FormLabel sx={{ display: "flex", alignItems: "center", mr: 2 }}>
                {t("showFailedResults") + " "}
                <Switch
                  size="small"
                  checked={showFailedResults}
                  onChange={() => {
                    setShowFailedResults(!showFailedResults);
                  }}
                />
              </FormLabel>
            ) : null}
          </Box>
        </Box>
      )}
      {!searchSuccessful && nonFallbackPlansToShow.length === 0 && !running ? (
        <FailedSearch failureReasons={errors} />
      ) : nonFallbackPlansToShow.length > 0 ? (
        <SuccessfulSearch
          plans={nonFallbackPlansToShow}
          incompleteResults={running}
          primary
          onClickPlan={onClickPlan}
        />
      ) : null}
      {fallbackPlans.length > 0 && (
        <Box sx={{ mt: 6 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            {t("fallbackPlans")}
          </Typography>
          <SuccessfulSearch
            plans={fallbackPlans}
            incompleteResults={running}
            primary={false}
            onClickPlan={onClickPlan}
          />
        </Box>
      )}
    </>
  );
};

const organisePlans = (
  plans: Plan[],
  showFailedResults: boolean,
  showIntermediateResults: boolean
) => {
  const isAppEditedPlan = (plan: Plan) =>
    plan.metadata.parent_plan_id !== null &&
    plan.metadata.plan_type !== "fallback" &&
    (plan.deployments.length === 0 ||
      // We exclude temporary edits from the supervisor view
      !plan.deployments.every((deployment) =>
        ["edited_standard", "edited_backup"].includes(
          deployment.deployment_type
        )
      ));
  const latestEditedPlans = groupBy(
    plans.filter(isAppEditedPlan),
    (plan) => plan.metadata.base_plan_id ?? -1,
    (plansWithSameBase) => Math.max(...plansWithSameBase.map((plan) => plan.id))
  );
  // Unless showing intermediate results, include only unedited plans or the
  // most recent edited plan in each chain. Always include fallback plans
  // regardless of intermediate results setting
  const filteredPlans = plans.filter(
    (plan) =>
      plan.metadata.parent_plan_id === null ||
      Object.values(latestEditedPlans).includes(plan.id) ||
      showIntermediateResults ||
      plan.metadata.plan_type === "fallback"
  );
  const sortedPlans = sorted(filteredPlans, (plan) => plan.plan_index);
  return sortedPlans.filter((plan) => shouldShowPlan(plan, showFailedResults));
};

const shouldShowPlan = (plan: Plan, showFailedResults: boolean) => {
  // Always show fallback and edited plans regardless of optimization status
  if (
    plan.metadata.plan_type === "fallback" ||
    plan.metadata.plan_type === "edited"
  ) {
    return true;
  }

  return (
    showFailedResults ||
    ["succeeded", "succeeded_suboptimal"].includes(plan.optimisation_status)
  );
};

const computeLongestSuboptimalTimeLimit = (
  plans: Plan[] | undefined
): number | undefined => {
  if (plans == null) {
    return undefined;
  }
  let longest: number | undefined = undefined;
  for (const plan of plans) {
    if (
      plan.optimisation_status === "failed_suboptimal" ||
      plan.optimisation_status === "succeeded_suboptimal"
    ) {
      longest =
        longest === undefined
          ? plan.time_limit
          : Math.max(longest, plan.time_limit);
    }
  }
  return longest;
};
